
import React from 'react';
import { 
  LayoutDashboard, 
  ShieldCheck, 
  Building2, 
  Bell, 
  Settings, 
  LogOut,
  Fingerprint,
  Mail,
  Phone,
  FileCheck,
  Search,
  ChevronRight,
  Leaf,
  BarChart3,
  Zap,
  Globe,
  Database,
  Link2,
  Wallet,
  CreditCard,
  TrendingUp,
  Ghost,
  Sun,
  Moon,
  MessageCircle
} from 'lucide-react';
import { ViewMode, NavigationPage, SubscriptionTier } from '../types';

interface LayoutProps {
  children: React.ReactNode;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  currentPage: NavigationPage;
  onNavigate: (page: NavigationPage) => void;
  plan: SubscriptionTier;
  onLogout: () => void;
  darkMode: boolean;
  setDarkMode: (val: boolean) => void;
}

export const Layout: React.FC<LayoutProps> = ({ 
  children, 
  viewMode, 
  setViewMode, 
  currentPage, 
  onNavigate, 
  plan, 
  onLogout,
  darkMode,
  setDarkMode
}) => {
  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden transition-colors duration-300">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col hidden md:flex border-r border-slate-800/50">
        <div className="p-6 border-b border-slate-800/50">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => onNavigate('dashboard')}>
            <img src="assets/logo.png" alt="Minerva Logo" className="w-8 h-8 object-contain shadow-lg" />
            <span className="text-xl font-bold tracking-tight">Minerva</span>
          </div>
          <div className="mt-2 inline-flex items-center space-x-1.5 px-2 py-1 rounded bg-slate-800 text-[10px] font-black uppercase text-blue-400 border border-slate-700">
            <Zap size={10} />
            <span>{plan} Plan</span>
          </div>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto no-scrollbar">
          <SidebarItem 
            icon={<LayoutDashboard size={18} />} 
            label="Dashboard" 
            active={currentPage === 'dashboard'} 
            onClick={() => onNavigate('dashboard')}
          />
          
          <div className="pt-4 pb-2 px-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Regulatory (AML/LEI)</div>
          <SidebarItem icon={<ShieldCheck size={18} />} label="AML Monitoring" active={currentPage === 'aml'} onClick={() => onNavigate('aml')} />
          <SidebarItem icon={<FileCheck size={18} />} label="LEI Registry" active={currentPage === 'lei'} onClick={() => onNavigate('lei')} />
          <SidebarItem icon={<Fingerprint size={18} />} label="Individual KYC" active={currentPage === 'ekyc'} onClick={() => onNavigate('ekyc')} />
          <SidebarItem icon={<Building2 size={18} />} label="KYB (Corporate)" active={currentPage === 'kyb'} onClick={() => onNavigate('kyb')} />
          
          <div className="pt-4 pb-2 px-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Fintech Intel</div>
          <SidebarItem icon={<Wallet size={18} />} label="Blockchain Intel" active={currentPage === 'blockchain'} onClick={() => onNavigate('blockchain')} />
          <SidebarItem icon={<CreditCard size={18} />} label="IBAN Monitoring" active={currentPage === 'iban'} onClick={() => onNavigate('iban')} />
          <SidebarItem icon={<TrendingUp size={18} />} label="Stock Market" active={currentPage === 'market'} onClick={() => onNavigate('market')} />
          
          <div className="pt-4 pb-2 px-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Intelligence Services</div>
          <SidebarItem icon={<Leaf size={18} />} label="ESG Risk Score" active={currentPage === 'esg'} onClick={() => onNavigate('esg')} />
          <SidebarItem icon={<Ghost size={18} />} label="Darkweb Monitor" active={currentPage === 'darkweb'} onClick={() => onNavigate('darkweb')} />
          <SidebarItem icon={<BarChart3 size={18} />} label="Market Sentiment" active={currentPage === 'reputation'} onClick={() => onNavigate('reputation')} />
          
          <div className="pt-4 pb-2 px-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Verification APIs</div>
          <SidebarItem icon={<Phone size={18} />} label="Phone Intelligence" active={currentPage === 'phone'} onClick={() => onNavigate('phone')} />
          <SidebarItem icon={<Mail size={18} />} label="Email Validation" active={currentPage === 'email'} onClick={() => onNavigate('email')} />
          
          <div className="pt-8 pb-2 px-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest opacity-50">Support</div>
          <SidebarItem icon={<MessageCircle size={18} />} label="Contact Hub" active={currentPage === 'contact'} onClick={() => onNavigate('contact')} />
          <SidebarItem icon={<Settings size={18} />} label="Settings" active={currentPage === 'settings'} onClick={() => onNavigate('settings')} />
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button 
            onClick={onLogout}
            className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors w-full px-2 py-2"
          >
            <LogOut size={18} />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-6 sticky top-0 z-10 transition-colors">
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-full px-4 py-2 w-96">
            <Search size={16} className="text-slate-400 mr-2" />
            <input 
              type="text" 
              placeholder="Search entities, ISINs or LEIs..." 
              className="bg-transparent border-none focus:ring-0 text-sm w-full outline-none dark:text-white placeholder:dark:text-slate-500"
            />
          </div>

          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
              <button onClick={() => setViewMode('user')} className={`px-3 py-1 rounded text-xs font-medium transition-all ${viewMode === 'user' ? 'bg-white dark:bg-slate-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`}>Officer</button>
              <button onClick={() => setViewMode('admin')} className={`px-3 py-1 rounded text-xs font-medium transition-all ${viewMode === 'admin' ? 'bg-white dark:bg-slate-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`}>Admin</button>
            </div>
            
            <button className="relative p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
              <Bell size={20} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full border-2 border-white dark:border-slate-900"></span>
            </button>
            
            <div className="flex items-center space-x-3 pl-4 border-l border-slate-200 dark:border-slate-800">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-slate-900 dark:text-white leading-tight">Julian Casablancas</p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase font-black">Senior Compliance</p>
              </div>
              <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=Julian`} alt="Avatar" className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm" />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-slate-50/50 dark:bg-slate-950/50 transition-colors">
          {children}
        </main>
      </div>
    </div>
  );
};

const SidebarItem: React.FC<{ icon: React.ReactNode, label: string, active?: boolean, onClick: () => void }> = ({ icon, label, active, onClick }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
      active 
        ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' 
        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
    }`}
  >
    {icon}
    <span>{label}</span>
  </button>
);
